// src/pages/SymbolAnalysis.js
import React, { useState, useEffect, useRef } from 'react';
import { useDataCache } from '../context/DataCacheContext';
import { getBars, getSentiment, getSentimentAggregate } from '../services/api';
import { ACTIVE_TRADING_SYMBOLS } from '../config/tradingConfig';
import { 
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie
} from 'recharts';
import './SymbolAnalysis.css';

const SymbolAnalysis = () => {
  const [symbols, setSymbols] = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [timeframe, setTimeframe] = useState('24');
  const [priceData, setPriceData] = useState([]);
  const [sentimentData, setSentimentData] = useState([]);
  const [sentimentAggregate, setSentimentAggregate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);
  const loadTimerRef = useRef(null);
  
  // Add cache hooks
  const { getCached, setCache, isCacheValid } = useDataCache();

  useEffect(() => {
    loadSymbols();
  // loadSymbols reads only constants and cache context — safe to run once on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedSymbol) {
      // Clear previous timer
      if (loadTimerRef.current) {
        clearTimeout(loadTimerRef.current);
      }

      // Debounce - wait 300ms before loading
      loadTimerRef.current = setTimeout(() => {
        loadSymbolData();
      }, 300);
    }

    return () => {
      if (loadTimerRef.current) {
        clearTimeout(loadTimerRef.current);
      }
    };
  // loadSymbolData is defined in component scope but only depends on selectedSymbol/timeframe
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSymbol, timeframe]);

  const loadSymbols = async () => {
    // Check cache first
    if (isCacheValid('symbols-list', 3600000)) { // Cache for 1 hour
      const cached = getCached('symbols-list');
      console.log('Using cached symbols list (15 actively traded)');
      setSymbols(cached.data);
      setSelectedSymbol(cached.data[0]);
      setLoading(false);
      return;
    }

    try {
      console.log('Loading active trading symbols...');
      
      // Use symbols from strategy config
      setSymbols(ACTIVE_TRADING_SYMBOLS);
      setSelectedSymbol(ACTIVE_TRADING_SYMBOLS[0]); // Default to GE (best performer)
      
      // Cache symbols list
      setCache('symbols-list', ACTIVE_TRADING_SYMBOLS);
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading symbols:', error);
      setError('Failed to load symbols: ' + error.message);
      setLoading(false);
    }
  };

  const loadSymbolData = async () => {
    const cacheKey = `symbol-${selectedSymbol}-${timeframe}`;
    
    // Check cache first
    if (isCacheValid(cacheKey, 300000)) { // Cache valid for 5 minutes
      const cached = getCached(cacheKey);
      console.log('Using cached data for:', selectedSymbol);
      setPriceData(cached.data.priceData || []);
      setSentimentData(cached.data.sentimentData || []);
      setSentimentAggregate(cached.data.sentimentAggregate || null);
      return;
    }

    setLoadingData(true);
    try {
      console.log('Loading fresh data for:', selectedSymbol);
      
      const [bars, sentiment, sentAgg] = await Promise.all([
        getBars(selectedSymbol, parseInt(timeframe)).catch(err => {
          console.error('Error loading bars:', err);
          return { bars: [] };
        }),
        getSentiment(selectedSymbol, 7).catch(err => {
          console.error('Error loading sentiment:', err);
          return { articles: [] };
        }),
        getSentimentAggregate(selectedSymbol, 30).catch(err => {
          console.error('Error loading sentiment aggregate:', err);
          return null;
        })
      ]);

      // Price data
      let chartData = [];
      if (bars.bars && bars.bars.length > 0) {
        chartData = bars.bars.map(bar => ({
          time: new Date(bar.timestamp).toLocaleString([], { 
            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
          }),
          price: bar.close,
          volume: bar.volume,
          high: bar.high,
          low: bar.low,
          open: bar.open
        }));
      }
      
      setPriceData(chartData);
      setSentimentData(sentiment.articles || []);
      setSentimentAggregate(sentAgg);
      
      // Cache the data
      setCache(cacheKey, {
        priceData: chartData,
        sentimentData: sentiment.articles || [],
        sentimentAggregate: sentAgg
      });
      
      setLoadingData(false);
    } catch (error) {
      console.error('Error loading symbol data:', error);
      setLoadingData(false);
    }
  };

  const timeframes = [
    { label: '1H', value: '1' },
    { label: '6H', value: '6' },
    { label: '24H', value: '24' },
    { label: '1W', value: '168' },
    { label: '1M', value: '720' }
  ];

  const sentimentColors = {
    positive: '#00ff88',
    neutral: '#8b92a7',
    negative: '#ff4757'
  };

  if (loading) {
    return <div className="loading">Loading data...</div>;
  }

  if (error) {
    return (
      <div className="error-state">
        <h1>Symbol Analysis</h1>
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          <button onClick={loadSymbols} className="retry-btn">Retry</button>
        </div>
      </div>
    );
  }

  const currentPrice = priceData.length > 0 ? priceData[priceData.length - 1].price : 0;
  const priceChange = priceData.length > 1 
    ? ((priceData[priceData.length - 1].price - priceData[0].price) / priceData[0].price) * 100 
    : 0;

  return (
    <div className="symbol-analysis">
      <div className="analysis-header">
        <div className="symbol-selector">
          <label>Symbol</label>
          <select 
            value={selectedSymbol} 
            onChange={(e) => {
              console.log('Selected symbol:', e.target.value);
              setSelectedSymbol(e.target.value);
            }}
            disabled={loadingData}
          >
            {symbols.length === 0 ? (
              <option value="">No symbols available</option>
            ) : (
              symbols.map(symbol => (
                <option key={symbol} value={symbol}>{symbol}</option>
              ))
            )}
          </select>
        </div>

        <div className="price-display">
          <div className="current-price">${currentPrice.toFixed(2)}</div>
          <div className={`price-change ${priceChange >= 0 ? 'positive' : 'negative'}`}>
            {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(2)}%
          </div>
        </div>

        <div className="timeframe-selector">
          {timeframes.map(tf => (
            <button
              key={tf.value}
              className={timeframe === tf.value ? 'tf-btn active' : 'tf-btn'}
              onClick={() => setTimeframe(tf.value)}
              disabled={loadingData}
            >
              {tf.label}
            </button>
          ))}
        </div>
      </div>

      {/* Price Chart */}
      <div className="chart-section">
        <h2>Price Chart</h2>
        {loadingData ? (
          <div className="chart-loading">Loading chart data...</div>
        ) : priceData.length === 0 ? (
          <div className="no-data-chart">No price data available for this timeframe</div>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={priceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a2332" />
              <XAxis dataKey="time" stroke="#8b92a7" tick={{fontSize: 11}} />
              <YAxis stroke="#8b92a7" domain={['auto', 'auto']} />
              <Tooltip 
                contentStyle={{ 
                  background: '#0f1729', 
                  border: '1px solid #1a2332', 
                  borderRadius: '8px' 
                }}
                labelStyle={{ color: '#8b92a7' }}
              />
              <Line 
                type="monotone" 
                dataKey="price" 
                stroke="#00d4ff" 
                strokeWidth={2} 
                dot={false}
                animationDuration={1000}
                animationEasing="ease-in-out"
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Volume Chart */}
      <div className="chart-section">
        <h2>Volume</h2>
        {loadingData ? (
          <div className="chart-loading">Loading volume data...</div>
        ) : priceData.length === 0 ? (
          <div className="no-data-chart">No volume data available for this timeframe</div>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={priceData} margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a2332" />
              <XAxis dataKey="time" stroke="#8b92a7" tick={{fontSize: 11}} />
              <YAxis
                stroke="#8b92a7"
                width={55}
                tickFormatter={(value) => {
                  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                  return value;
                }}
              />
              <Tooltip 
                contentStyle={{ 
                  background: '#0f1729', 
                  border: '1px solid #1a2332', 
                  borderRadius: '8px' 
                }}
              />
              <Bar 
                dataKey="volume" 
                fill="#00d4ff" 
                opacity={0.6}
                animationDuration={800}
                animationEasing="ease-in-out"
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Sentiment Section */}
      {sentimentAggregate && sentimentAggregate.total_articles > 0 && (
        <div className="sentiment-section">
          <h2>News Sentiment (Last 30 Days)</h2>
          
          <div className="sentiment-overview">
            <div className="sentiment-chart">
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={sentimentAggregate.sentiment_breakdown.map(entry => ({
                      ...entry,
                      fill: sentimentColors[entry.sentiment]
                    }))}
                    dataKey="count"
                    nameKey="sentiment"
                    cx="50%"
                    cy="50%"
                    outerRadius={85}
                    label={(entry) => `${entry.sentiment}: ${entry.count}`}
                  />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="sentiment-stats">
              <div className="sent-stat">
                <span className="sent-label">Total Articles</span>
                <span className="sent-value">{sentimentAggregate.total_articles}</span>
              </div>
              <div className="sent-stat positive">
                <span className="sent-label">Positive</span>
                <span className="sent-value">{sentimentAggregate.positive}</span>
              </div>
              <div className="sent-stat neutral">
                <span className="sent-label">Neutral</span>
                <span className="sent-value">{sentimentAggregate.neutral}</span>
              </div>
              <div className="sent-stat negative">
                <span className="sent-label">Negative</span>
                <span className="sent-value">{sentimentAggregate.negative}</span>
              </div>
            </div>
          </div>

          {/* Recent News */}
          <div className="news-feed">
            <h3>Recent News (Last 7 Days)</h3>
            {sentimentData.length === 0 ? (
              <p className="no-news">No recent news articles</p>
            ) : (
              sentimentData.slice(0, 10).map((article, idx) => (
                <div key={idx} className="news-item">
                  <div className="news-header">
                    <span className={`news-sentiment ${article.sentiment}`}>
                      {article.sentiment.toUpperCase()}
                    </span>
                    <span className="news-date">
                      {new Date(article.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                  <h4 className="news-headline">{article.headline}</h4>
                  {article.summary && (
                    <p className="news-summary">{article.summary}</p>
                  )}
                  {article.url && (
                    <a 
                      href={article.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="news-link"
                    >
                      Read more →
                    </a>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SymbolAnalysis;